import { Link } from "react-router-dom";
import { ShieldCheck, Eye, Lightbulb, BarChart3, Search, Flag, Activity, FileCheck } from "lucide-react";
import { useT } from "@/lib/i18n";

export default function Landing() {
  const { t } = useT();
  const pillars = [
    { icon: Lightbulb, key: "landing.pillar_inspire" },
    { icon: BarChart3, key: "landing.pillar_develop" },
    { icon: Search, key: "landing.pillar_discover" },
    { icon: Flag, key: "landing.pillar_lead" },
  ];

  return (
    <div
      className="flex flex-col overflow-hidden relative"
      style={{ height: "calc(100vh - 73px)" }}
      data-testid="landing-page"
    >
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(900px 500px at 50% -10%, #eef2fb 0%, transparent 60%), radial-gradient(700px 400px at 85% 45%, #f7eec9 0%, transparent 55%), #ffffff",
        }}
      />

      {/* Hero — IDDL full-text brand is the focus */}
      <section className="flex-1 min-h-0 flex flex-col justify-center items-center px-6 md:px-12 lg:px-20 pb-4">
        <div className="label-mono text-center" style={{ color: "var(--iddl-ink)" }}>
          {t("landing.badge")}
        </div>

        {/* Full-text IDDL brand — replaces the stylized iDDL lockup */}
        <h1
          className="font-display font-extrabold tracking-tight text-center mt-4 leading-[1.02]"
          style={{ color: "var(--iddl-ink)" }}
          data-testid="iddl-brand-full"
        >
          <span className="block text-3xl sm:text-4xl md:text-5xl lg:text-6xl">
            {t("landing.brand_full")}
          </span>
        </h1>

        {/* Three light-blue domain pills — replaces the old dotted subline */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3 md:gap-4">
          <span className="domain-pill" data-testid="domain-pill-ds">{t("landing.domain_ds")}</span>
          <span className="domain-pill" data-testid="domain-pill-cs">{t("landing.domain_cs")}</span>
          <span className="domain-pill" data-testid="domain-pill-ai">{t("landing.domain_ai")}</span>
        </div>

        {/* Tagline with gold rule lines */}
        <div className="mt-6 mx-auto max-w-2xl flex items-center gap-3">
          <span className="flex-1 h-px" style={{ background: "var(--iddl-gold)" }} />
          <span
            className="font-display font-semibold tracking-wide text-sm md:text-base text-center"
            style={{ color: "var(--iddl-ink)" }}
          >
            {t("landing.tagline")}
          </span>
          <span className="flex-1 h-px" style={{ background: "var(--iddl-gold)" }} />
        </div>

        {/* Two 3:1 buttons — wider, shorter */}
        <div className="mt-7 flex flex-wrap items-center justify-center gap-5">
          <Link
            to="/start"
            className="btn-3d btn-3d-navy btn-3d-wide"
            data-testid="btn-candidate"
            aria-label={t("landing.btn_candidate")}
          >
            <ShieldCheck size={16} strokeWidth={2.2} />
            <span>{t("landing.btn_candidate")}</span>
          </Link>
          <Link
            to="/admin"
            className="btn-3d btn-3d-gold btn-3d-wide"
            data-testid="btn-admin"
            aria-label={t("landing.btn_admin")}
          >
            <Eye size={16} strokeWidth={2.2} />
            <span>{t("landing.btn_admin")}</span>
          </Link>
        </div>
      </section>

      {/* Pillar strip */}
      <section className="shrink-0 border-t border-b" style={{ borderColor: "var(--iddl-gold)" }}>
        <div className="max-w-7xl mx-auto grid grid-cols-4">
          {pillars.map((p, i) => (
            <div
              key={p.key}
              className={`flex items-center justify-center gap-3 py-2.5 ${i < 3 ? "border-r" : ""}`}
              style={{ borderColor: "var(--iddl-rule)" }}
              data-testid={`pillar-${p.key}`}
            >
              <p.icon size={15} style={{ color: "var(--iddl-gold-deep)" }} />
              <div
                className="font-display font-bold tracking-[0.2em] text-xs"
                style={{ color: "var(--iddl-ink)" }}
              >
                {t(p.key)}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Bottom info strip */}
      <section className="shrink-0 border-t border-[var(--iddl-rule)] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-2.5 flex flex-wrap items-center gap-x-6 gap-y-1 text-[11px] text-[var(--iddl-muted)]">
          <span className="flex items-center gap-1.5">
            <Eye size={11} /> <b style={{ color: "var(--iddl-ink)" }}>{t("landing.foot_know")}</b>
          </span>
          <span className="flex items-center gap-1.5">
            <Activity size={11} /> <b style={{ color: "var(--iddl-ink)" }}>{t("landing.foot_perform")}</b>
          </span>
          <span className="flex items-center gap-1.5">
            <FileCheck size={11} /> <b style={{ color: "var(--iddl-ink)" }}>{t("landing.foot_reason")}</b>
          </span>
          <span className="hidden md:inline mx-1" style={{ color: "var(--iddl-rule)" }}>|</span>
          <span className="flex items-center gap-1.5">
            <ShieldCheck size={11} /> {t("landing.foot_sequence")}
          </span>
          <span className="hidden md:inline">{t("landing.foot_result")}</span>
          <span className="hidden lg:inline">{t("landing.foot_license")}</span>
        </div>
      </section>
    </div>
  );
}
